import { HERO_ACTION_SOURCE_FRAME_COUNT, heroActionSourceFrame } from '@/modules/game-domain/hero-action-timeline';
import type { Texture } from 'pixi.js';
import { AnimationAtlasCache } from './animation-atlas-cache';
import { tryResolveGameAssetFile, type GameTextureManifestEntry } from '@/game/assets/resolveGameAssetUrl';
import type { HeroAnimationFacing, HeroAnimationView } from '@/modules/game-domain/render-snapshot';
import { meshyHeroClip, meshyClipFrame, type MeshyClip, type MeshyHeroClip } from './meshy-hero-clips';

const SOURCE_OFFSETS = { east: 37, west: 37, south: 35, north: 34 };
/** Whole approved artwork only; all Sprite/Texture views share the Assets source. */
export class HeroAnimationAssets extends AnimationAtlasCache {
  definition(variant: number, clip: MeshyClip, facing: HeroAnimationFacing): MeshyHeroClip | undefined {
    return meshyHeroClip(variant, clip, facing);
  }
  get(clip: MeshyClip, facing: HeroAnimationFacing, originalSize: { width: number; height: number }, variant = 0): readonly Texture[] | null {
    const definition = variant === -1 ? undefined : this.definition(variant, clip, facing);
    const file = definition?.file ?? `world-art/animations/hero-0-${clip}-${facing}.png`;
    if (!definition && variant !== 0 && variant !== -1) return null;
    const url = tryResolveGameAssetFile(file);
    if (!url) return null;
    const width = definition?.width ?? (clip === 'walk' ? originalSize.width : 128), height = definition?.height ?? (clip === 'walk' ? originalSize.height : 144);
    const columns = definition?.frames ?? (clip === 'combat-idle' ? 1 : HERO_ACTION_SOURCE_FRAME_COUNT);
    return this.requestAtlas({ url, width, height, columns, scaleMode: 'nearest' });
  }
}
export function wholeAnimationPresentation(original: NonNullable<GameTextureManifestEntry['presentation']>, facing: HeroAnimationFacing, combat: boolean): GameTextureManifestEntry {
  const cardinal = facing.includes('east') ? 'east' : facing.includes('west') ? 'west' : facing as keyof typeof SOURCE_OFFSETS;
  const offsetX = combat ? SOURCE_OFFSETS[cardinal] : 0, offsetY = combat ? 32 : 0;
  return { file: '', kind: 'character', presentation: {
    bodyBoundsPx: { ...original.bodyBoundsPx, x: original.bodyBoundsPx.x + offsetX, y: original.bodyBoundsPx.y + offsetY },
    groundContactPx: { x: original.groundContactPx.x + offsetX, y: original.groundContactPx.y + offsetY },
    displayHeightPx: original.displayHeightPx,
  } };
}
export function wholeAnimationFrame(frames: readonly Texture[], motion: HeroAnimationView, definition?: MeshyHeroClip): Texture | null {
  if (frames.length === 1) return frames[0] ?? null;
  const progress = motion.progress ?? motion.frame / HERO_ACTION_SOURCE_FRAME_COUNT;
  if (definition) return frames[motion.progress === undefined ? Math.max(0, Math.min(frames.length - 1, motion.frame)) : meshyClipFrame(definition, progress)] ?? null;
  if (motion.clip === 'attack' || motion.clip === 'block' || motion.clip === 'dodge') {
    const index = motion.progress === undefined ? motion.frame : heroActionSourceFrame(motion.clip, progress, motion.facing);
    return frames[index] ?? null;
  }
  const index = Math.min(frames.length - 1, Math.max(0, Math.floor(progress * frames.length)));
  return frames[index] ?? null;
}
